import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as UserIcon,
  Refresh as RetryIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { ChatMessage as ChatMessageType } from './AIChatContext';

/**
 * ChatMessage Component
 *
 * Renders individual chat messages with:
 * - User messages: right-aligned, primary color
 * - AI messages: left-aligned, secondary color
 * - Avatar icons
 * - Timestamps
 * - Loading/error states
 */

interface ChatMessageProps {
  message: ChatMessageType;
  onRetry?: () => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onRetry }) => {
  const isUser = message.role === 'user';
  const isLoading = message.status === 'sending' && message.role === 'assistant';
  const hasError = message.status === 'error';

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 2,
        maxWidth: '100%',
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: isUser ? 'primary.main' : 'secondary.main',
          flexShrink: 0,
        }}
      >
        {isUser ? (
          <UserIcon sx={{ fontSize: 18 }} />
        ) : (
          <BotIcon sx={{ fontSize: 18 }} />
        )}
      </Avatar>

      {/* Message Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          maxWidth: 'calc(100% - 48px)',
        }}
      >
        {/* Message Bubble */}
        <Box
          sx={{
            bgcolor: hasError
              ? 'error.light'
              : isUser
              ? 'primary.main'
              : 'grey.100',
            color: hasError
              ? 'error.contrastText'
              : isUser
              ? 'primary.contrastText'
              : 'text.primary',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            maxWidth: '100%',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            position: 'relative',
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          ) : hasError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                {message.error || 'An error occurred'}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              {message.content}
            </Typography>
          )}
        </Box>

        {/* Timestamp and Retry */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.7rem' }}
          >
            {formatTime(message.timestamp)}
          </Typography>

          {hasError && isUser && onRetry && (
            <Tooltip title="Retry">
              <IconButton
                size="small"
                onClick={onRetry}
                sx={{
                  p: 0.25,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <RetryIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;
